import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Observable } from "rxjs";
import swal from "sweetalert2";

import { VacacionesService } from "../vacaciones.service";
import { PermisosService } from "../../permisos/permisos.service";
import { DIAS_HABILES, semanasHabiles } from "../semanas-habiles";
import { CargaExcelVacacionesComponent } from "../carga-excel-vacaciones/carga-excel-vacaciones.component";
import { FrcFirmadoComponent } from "../frc-firmado/frc-firmado.component";
import {
  permisoConFrc,
  rhCargaPermiso,
  rhCargaVacacion,
  textoDocumento,
  vacacionConFrc,
} from "../frc-firmado/documento-rh";
import { idEnSesion } from "../frc-firmado/sesion";
import { generaFrc019 } from "../frc019";
import { agrupaPedidos, tramosDe } from "../pedidos-vacaciones";
import {
  acumuladoUsado,
  acumuladoVista,
  gozadosEnCos,
  gozadosVista,
  paraElSiguiente,
  quedanVista,
  restantesVista,
} from "../saldo-vista";
import { generaFrc017 } from "../../permisos/frc017";
import {
  AlcanceSeguimiento,
  notaVisible,
  Saldo,
  Vacacion,
} from "../../shared/model/vacaciones/vacacion.model";
import { Permiso } from "../../shared/model/permisos/permiso.model";

type Pestana = "calendario" | "vacaciones" | "permisos" | "personas";

/** Una celda del mes: el día con las vacaciones y los permisos que le tocan. */
interface DiaSeg {
  iso: string | null;
  numero: number | null;
  esFestivo: boolean;
  esHoy: boolean;
  vacaciones: Vacacion[];
  permisos: Permiso[];
}

/**
 * El seguimiento de vacaciones y permisos. En «Mi equipo» es de SOLO
 * LECTURA; en Seguimiento RH, además, RH sube el Excel y gestiona el
 * documento de cada solicitud (ver abajo).
 *
 * Es una pantalla con dos alcances, y el alcance lo dice la ruta por la
 * que se entró:
 *
 *  - "Mi equipo" (`admin/vacaciones/equipo`): el líder ve las vacaciones
 *    y los permisos de la gente que autoriza, y nada más.
 *  - "Seguimiento RH" (`admin/vacaciones/seguimiento`): toda la empresa,
 *    para que RH lleve el seguimiento de lo que se va pidiendo,
 *    autorizando y negando.
 *
 * Aquí no se responde nada: el líder resuelve desde «Vacaciones y
 * Permisos» y quien administra desde el Calendario. Por eso no hay
 * botones de resolver, ni firma, ni goce que escoger. Lo que la pantalla
 * pide es una intención; el alcance de verdad lo concede el servidor
 * según los menús del usuario, así que entrar por la URL no enseña de más.
 *
 * Lo que se escribe desde aquí es sólo de Seguimiento RH. La carga del
 * Excel de RH (app-carga-excel-vacaciones en la barra): RH sube cada área
 * con el mismo formato de hoja. El servidor la deja con este menú o con
 * el del Calendario.
 *
 * Y, desde el 15/10/2026 (queja de Irving: RH no veía el documento de
 * cada solicitud y el líder y quien pidió sí), la columna «Documento» de
 * Vacaciones y Permisos: RH baja el FRC-019 o el FRC-017, ve y descarga
 * lo que se subió (con su historial) y carga uno nuevo en lo que sigue
 * vivo, aunque no se haya firmado en papel; lo anterior queda en el
 * historial. El servidor se lo deja a quien gestiona (gestionaDocumentos:
 * este menú o el del Calendario) y sólo con alcance TODOS manda el
 * firmado de cada fila. En «Mi equipo» no cambia nada.
 *
 * Desde el 24/09/2026 el saldo va por PERIODO DE ANIVERSARIO (del Alta
 * IMSS al siguiente aniversario), igual que la hoja de RH. Calendario,
 * Vacaciones y Permisos siguen siendo del año calendario que se elige;
 * Personas enseña, por persona, el periodo vigente a la fecha de
 * referencia de ese año (ver fechaRef).
 */
@Component({
  selector: "app-seguimiento-vacaciones",
  standalone: true,
  templateUrl: "./seguimiento-vacaciones.component.html",
  styleUrls: ["./seguimiento-vacaciones.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTooltipModule,
    CargaExcelVacacionesComponent,
    FrcFirmadoComponent,
  ],
})
export class SeguimientoVacacionesComponent implements OnInit {
  /** El número del día de hoy, para la muestra «Hoy» de la leyenda del calendario. */
  readonly hoyDia = new Date().getDate();

  /** Lo que la ruta pide. */
  readonly alcancePedido: AlcanceSeguimiento;
  /** Lo que el servidor concedió. Nulo hasta que contesta. */
  alcance: AlcanceSeguimiento | null = null;

  anio = new Date().getFullYear();
  /** El mes que se ve en el calendario; siempre dentro de `anio`. */
  mes = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  /**
   * La fecha a la que se enseña el periodo de cada quien en Personas (ISO).
   * Es la MISMA regla del servidor (VacacionBuss.fechaReferencia): hoy si
   * se ve el año actual, el 31/12 si es uno pasado y el 1 de enero si es
   * uno futuro. Si no coincidieran, el encabezado diría una fecha y la
   * tabla enseñaría los periodos de otra.
   */
  fechaRef = this.referenciaDe(this.anio);

  pestana: Pestana = "calendario";
  cargando = false;
  error = "";

  vacaciones: Vacacion[] = [];
  /**
   * Las vacaciones como solicitudes, para la tabla: los tramos de un pedido
   * de días sueltos, juntos (15/10/2026). `vacaciones` se queda tramo por
   * tramo para el calendario.
   */
  solicitudes: Vacacion[] = [];
  permisos: Permiso[] = [];
  saldos: Saldo[] = [];
  festivos = new Set<string>();

  /** Área por persona, sacada de los saldos. */
  private areaPor = new Map<number, string>();

  /* ---- Lo derivado. Son CAMPOS y no getters a propósito: un getter que
     arma un arreglo nuevo en cada ciclo de detección repinta sin parar.
     Se recalculan al cargar y al tocar un filtro. ---- */
  /** Las semanas del mes, de lunes a viernes. */
  semanas: DiaSeg[][] = [];
  /** Los mismos días en una sola lista, para las cuentas del mes. */
  dias: DiaSeg[] = [];
  readonly encabezadosCal = DIAS_HABILES;
  vacFiltradas: Vacacion[] = [];
  perFiltrados: Permiso[] = [];
  personasFiltradas: Saldo[] = [];

  /* ---- Filtros ---- */
  fEstatusVac = "";
  fTextoVac = "";
  fTipoPer = "";
  fEstatusPer = "";
  fTextoPer = "";
  fTextoPer2 = "";
  fTextoPersona = "";
  /** Área elegida en Personas ("" = todas; SIN_AREA = quien no tiene). */
  fAreaPersona = "";
  /** Tramo de antigüedad elegido en Personas ("" = todos). Ver tramoAntiguedad. */
  fAntigPersona = "";
  /**
   * Las opciones de los dos filtros de Personas, con cuántos hay en cada
   * una. Son CAMPOS por la misma razón que las listas filtradas: se arman
   * al cargar, no en cada ciclo de detección.
   */
  areasPersonas: { valor: string; texto: string; n: number }[] = [];
  antigPersonas: { valor: string; texto: string; n: number }[] = [];
  /** El número de «Todas las áreas»: los que pasan la antigüedad y la búsqueda. */
  totalAreasPersonas = 0;
  readonly SIN_AREA = "__sin_area__";

  /**
   * Los tramos de antigüedad de la ley (art. 76 LFT), que son los que
   * deciden los días: del 1 al 5 cada año suma dos días, y de ahí cada
   * cinco años. Los mismos números que VacacionBuss.diasPorLey.
   */
  private readonly TRAMOS = [
    { valor: "0", texto: "Primer año · aún sin días" },
    { valor: "1", texto: "1 año · 12 días" },
    { valor: "2", texto: "2 años · 14 días" },
    { valor: "3", texto: "3 años · 16 días" },
    { valor: "4", texto: "4 años · 18 días" },
    { valor: "5", texto: "5 años · 20 días" },
    { valor: "6-10", texto: "6 a 10 años · 22 días" },
    { valor: "11-15", texto: "11 a 15 años · 24 días" },
    { valor: "16-20", texto: "16 a 20 años · 26 días" },
    { valor: "21-25", texto: "21 a 25 años · 28 días" },
    { valor: "26+", texto: "26 años o más · 30 días o más" },
    { valor: "sin", texto: "Sin antigüedad (año calendario)" },
  ];

  /* ---- Paginado: ninguna tabla crece hacia abajo sin tope ---- */
  readonly POR_PAGINA = 10;
  paginaVac = 1;
  paginaPer = 1;
  paginaPersona = 1;

  readonly ESTATUS_VAC = [
    { valor: "PENDIENTE", texto: "Esperando", color: "#b45309" },
    { valor: "ACTIVO", texto: "Autorizada", color: "#15803d" },
    { valor: "RECHAZADA", texto: "No autorizada", color: "#b91c1c" },
    { valor: "CANCELADO", texto: "Retirada", color: "#64748b" },
  ];

  readonly ESTATUS_PER = [
    { valor: "PENDIENTE", texto: "Esperando", color: "#b45309" },
    { valor: "AUTORIZADO", texto: "Autorizado", color: "#15803d" },
    { valor: "RECHAZADO", texto: "No autorizado", color: "#b91c1c" },
    { valor: "CANCELADO", texto: "Retirado", color: "#64748b" },
  ];

  readonly TIPOS = [
    { valor: "FALTA", texto: "Falta el día", icono: "event_busy" },
    { valor: "SALIDA", texto: "Sale antes", icono: "logout" },
    { valor: "ENTRADA", texto: "Llega tarde", icono: "login" },
  ];

  readonly MESES = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];

  private readonly MESES_CORTOS = [
    "ene", "feb", "mar", "abr", "may", "jun",
    "jul", "ago", "sep", "oct", "nov", "dic",
  ];

  /* ---- El documento de cada solicitud (15/10/2026), sólo en RH ----
     Las reglas viven en documento-rh.ts para que el Calendario diga lo
     mismo; aquí se exponen a la plantilla. */
  readonly vacacionConFrc = vacacionConFrc;
  readonly permisoConFrc = permisoConFrc;
  readonly rhCargaVacacion = rhCargaVacacion;
  readonly rhCargaPermiso = rhCargaPermiso;
  /** Quien mira: en lo suyo no se le ofrece cargar (eso va por «Mis vacaciones»). */
  readonly miId = idEnSesion();
  /** El FRC que se está armando («V12», «P7»), para apagar su botón. */
  generandoFrc: string | null = null;

  constructor(
    private service: VacacionesService,
    private readonly permisosService: PermisosService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {
    this.alcancePedido = this.route.snapshot.data["alcance"] === "TODOS" ? "TODOS" : "EQUIPO";
  }

  ngOnInit(): void {
    this.cargar();
  }

  /* ===================== Carga ===================== */

  cargar(): void {
    this.cargando = true;
    this.error = "";
    this.fechaRef = this.referenciaDe(this.anio);
    this.service.seguimiento(this.anio, this.alcancePedido).subscribe({
      next: (res) => {
        this.cargando = false;
        if (res.codigoError) {
          this.error = res.mensajeError || "No se pudo consultar el seguimiento.";
          this.cdr.detectChanges();
          return;
        }
        this.alcance = res.alcance || this.alcancePedido;
        this.vacaciones = res.vacaciones || [];
        this.solicitudes = agrupaPedidos(this.vacaciones);
        this.permisos = res.permisos || [];
        this.saldos = this.unoPorPersona(res.saldos || []);
        this.festivos = new Set<string>(res.festivos || []);

        this.areaPor.clear();
        for (const s of this.saldos) {
          if (s.idUsuario != null && s.area) {
            this.areaPor.set(s.idUsuario, s.area);
          }
        }

        this.paginaVac = 1;
        this.paginaPer = 1;
        this.paginaPersona = 1;
        this.recalcula();
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        this.error = "No se pudo conectar con el servidor.";
        this.cdr.detectChanges();
      },
    });
  }

  private recalcula(): void {
    this.construyeMes();
    this.filtraVacaciones();
    this.filtraPermisos();
    this.armaFiltrosPersonas();
    this.filtraPersonas();
  }

  /* ===================== Encabezado ===================== */

  get esRh(): boolean {
    return this.alcance === "TODOS";
  }

  get titulo(): string {
    return this.esRh ? "Seguimiento" : "Mi equipo";
  }

  /** Una frase corta: el chip «sólo consulta» del título ya dice lo demás. */
  get subtitulo(): string {
    if (this.esRh) {
      return "Lo que se pidió, autorizó y negó de toda la empresa.";
    }
    return "Vacaciones y permisos de la gente que autorizas.";
  }

  /** Cuántas personas abarca lo que se está viendo. */
  get personas(): number {
    return this.saldos.length;
  }

  /**
   * El año elige el calendario, las vacaciones y los permisos; el saldo
   * no es «del año» sino de su periodo de aniversario. El tooltip del
   * navegador lo dice para que nadie lea «2026» como «los días de 2026».
   */
  get tipAnio(): string {
    return `Calendario, vacaciones y permisos de ${this.anio}. ` +
      `En Personas, el periodo de cada quien vigente al ${this.fechaDma(this.fechaRef)}.`;
  }

  /** Por qué Personas enseña los periodos a esa fecha y no a otra. */
  get tipReferencia(): string {
    const actual = new Date().getFullYear();
    if (this.anio === actual) {
      return "Hoy: el periodo de aniversario en el que va cada persona.";
    }
    if (this.anio < actual) {
      return `Al cierre de ${this.anio}: el periodo en el que iba cada persona el 31 de diciembre.`;
    }
    return `Al empezar ${this.anio}: el periodo en el que irá cada persona el 1 de enero.`;
  }

  /** Misma regla que VacacionBuss.fechaReferencia (ver fechaRef). */
  private referenciaDe(anio: number): string {
    const hoy = new Date();
    if (anio === hoy.getFullYear()) {
      return this.iso(hoy);
    }
    return anio < hoy.getFullYear() ? `${anio}-12-31` : `${anio}-01-01`;
  }

  /* ===================== Navegación ===================== */

  mueveAnio(delta: number): void {
    this.anio += delta;
    this.mes = new Date(this.anio, this.mes.getMonth(), 1);
    this.cargar();
  }

  /**
   * El mes se mueve dentro del año cargado. Si se sale, se carga el otro
   * año: los datos son por año y no hay que traerlos de nuevo por cada
   * mes.
   */
  mueveMes(delta: number): void {
    const nuevo = new Date(this.mes.getFullYear(), this.mes.getMonth() + delta, 1);
    if (nuevo.getFullYear() !== this.anio) {
      this.anio = nuevo.getFullYear();
      this.mes = nuevo;
      this.cargar();
      return;
    }
    this.mes = nuevo;
    this.construyeMes();
  }

  irAHoy(): void {
    const hoy = new Date();
    if (hoy.getFullYear() !== this.anio) {
      this.anio = hoy.getFullYear();
      this.mes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      this.cargar();
      return;
    }
    this.mes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    this.construyeMes();
  }

  get tituloMes(): string {
    return `${this.MESES[this.mes.getMonth()]} ${this.mes.getFullYear()}`;
  }

  verPestana(cual: Pestana): void {
    this.pestana = cual;
  }

  /* ===================== Calendario ===================== */

  /**
   * La rejilla del mes, de lunes a viernes: el fin de semana no se trabaja
   * y no se pinta. Cada día lleva las vacaciones que lo cubren
   * (autorizadas y esperando) y los permisos de ese día (autorizados y
   * esperando). Lo negado y lo retirado no se pinta: el calendario dice
   * quién va a estar fuera, no quién lo pidió. Eso es cosa de las tablas.
   */
  private construyeMes(): void {
    const hoy = this.iso(new Date());

    const vivas = this.vacaciones.filter(
      (v) => v.estatus === "ACTIVO" || v.estatus === "PENDIENTE",
    );
    const permisosVivos = this.permisos.filter(
      (p) => p.estatus === "AUTORIZADO" || p.estatus === "PENDIENTE",
    );

    this.semanas = semanasHabiles<DiaSeg>(
      this.mes.getFullYear(),
      this.mes.getMonth(),
      (fecha) => {
        const iso = this.iso(fecha);
        const festivo = this.festivos.has(iso);
        return {
          iso,
          numero: fecha.getDate(),
          esFestivo: festivo,
          esHoy: iso === hoy,
          // El festivo no se pinta: no se trabaja, así que no es vacación
          // de nadie aunque el periodo lo abarque.
          vacaciones: festivo
            ? []
            : vivas.filter(
                (v) => !!v.fechaInicio && !!v.fechaFin && iso >= v.fechaInicio && iso <= v.fechaFin,
              ),
          permisos: permisosVivos.filter((p) => p.fecha === iso),
        };
      },
      () => ({
        iso: null, numero: null, esFestivo: false,
        esHoy: false, vacaciones: [], permisos: [],
      }),
    );
    this.dias = this.semanas.reduce((todos, s) => todos.concat(s), [] as DiaSeg[]);
  }

  /** Cuánta gente está fuera (o por salir) en el mes que se ve. */
  get fueraEsteMes(): number {
    const ids = new Set<number>();
    for (const d of this.dias) {
      for (const v of d.vacaciones) {
        if (v.idUsuario != null) {
          ids.add(v.idUsuario);
        }
      }
    }
    return ids.size;
  }

  get permisosEsteMes(): number {
    let n = 0;
    for (const d of this.dias) {
      n += d.permisos.length;
    }
    return n;
  }

  /* ===================== Vacaciones ===================== */

  filtraVacaciones(): void {
    const t = this.fTextoVac.trim().toLowerCase();
    this.vacFiltradas = this.solicitudes.filter((v) => {
      if (this.fEstatusVac && v.estatus !== this.fEstatusVac) {
        return false;
      }
      if (t) {
        const pajar = `${v.colaborador || ""} ${this.areaDe(v.idUsuario)} ${v.motivo || ""} ${notaVisible(v.comentario)}`
          .toLowerCase();
        if (!pajar.includes(t)) {
          return false;
        }
      }
      return true;
    });
    this.paginaVac = 1;
  }

  get hayFiltroVac(): boolean {
    return !!this.fEstatusVac || !!this.fTextoVac.trim();
  }

  limpiaVac(): void {
    this.fEstatusVac = "";
    this.fTextoVac = "";
    this.filtraVacaciones();
  }

  cuantasVac(estatus: string): number {
    let n = 0;
    for (const v of this.solicitudes) {
      if (v.estatus === estatus) {
        n++;
      }
    }
    return n;
  }

  etiquetaVac(e?: string): string {
    const x = this.ESTATUS_VAC.find((s) => s.valor === e);
    return x ? x.texto : e || "";
  }

  colorVac(e?: string): string {
    const x = this.ESTATUS_VAC.find((s) => s.valor === e);
    return x ? x.color : "#64748b";
  }

  /** "Irving Guzmán · 12/09 14:30", o vacío si nadie la ha resuelto. */
  resolucion(v: Vacacion): string {
    if (!v.autorizaNombre) {
      return "";
    }
    return v.fechaResolucion
      ? `${v.autorizaNombre} · ${this.fechaHoraCorta(v.fechaResolucion)}`
      : v.autorizaNombre;
  }

  /* ===================== Permisos ===================== */

  filtraPermisos(): void {
    const t = this.fTextoPer.trim().toLowerCase();
    this.perFiltrados = this.permisos.filter((p) => {
      if (this.fTipoPer && p.tipo !== this.fTipoPer) {
        return false;
      }
      if (this.fEstatusPer && p.estatus !== this.fEstatusPer) {
        return false;
      }
      if (t) {
        const pajar = `${p.colaborador || ""} ${p.area || ""} ${p.observaciones || ""} ${p.motivo || ""}`
          .toLowerCase();
        if (!pajar.includes(t)) {
          return false;
        }
      }
      return true;
    });
    this.paginaPer = 1;
  }

  get hayFiltroPer(): boolean {
    return !!this.fTipoPer || !!this.fEstatusPer || !!this.fTextoPer.trim();
  }

  limpiaPer(): void {
    this.fTipoPer = "";
    this.fEstatusPer = "";
    this.fTextoPer = "";
    this.filtraPermisos();
  }

  cuantosPer(estatus: string): number {
    let n = 0;
    for (const p of this.permisos) {
      if (p.estatus === estatus) {
        n++;
      }
    }
    return n;
  }

  etiquetaPer(e?: string): string {
    const x = this.ESTATUS_PER.find((s) => s.valor === e);
    return x ? x.texto : e || "";
  }

  colorPer(e?: string): string {
    const x = this.ESTATUS_PER.find((s) => s.valor === e);
    return x ? x.color : "#64748b";
  }

  iconoTipo(p: Permiso): string {
    const x = this.TIPOS.find((t) => t.valor === p.tipo);
    return x ? x.icono : "assignment";
  }

  /** "Sale antes · 14:30" / "Falta el día". */
  textoTipo(p: Permiso): string {
    const x = this.TIPOS.find((t) => t.valor === p.tipo);
    const base = x ? x.texto : p.tipo || "";
    return p.hora && p.tipo !== "FALTA" ? `${base} · ${p.hora}` : base;
  }

  resolucionPer(p: Permiso): string {
    if (!p.autorizaNombre) {
      return "";
    }
    return p.fechaResolucion
      ? `${p.autorizaNombre} · ${this.fechaHoraCorta(p.fechaResolucion)}`
      : p.autorizaNombre;
  }

  /* ===================== Personas ===================== */

  filtraPersonas(): void {
    const t = this.fTextoPersona.trim().toLowerCase();
    this.personasFiltradas = this.saldos.filter(
      (s) => this.pasaAreaPersona(s) && this.pasaAntigPersona(s) && this.pasaTextoPersona(s, t),
    );
    this.cuentaFiltrosPersonas(t);
    this.paginaPersona = 1;
  }

  private pasaAreaPersona(s: Saldo): boolean {
    return !this.fAreaPersona || this.claveArea(s) === this.fAreaPersona;
  }

  private pasaAntigPersona(s: Saldo): boolean {
    return !this.fAntigPersona || this.tramoAntiguedad(s) === this.fAntigPersona;
  }

  /** `t` ya viene recortado y en minúsculas. */
  private pasaTextoPersona(s: Saldo, t: string): boolean {
    if (!t) {
      return true;
    }
    // Quien autoriza sólo cuenta en RH, que es donde se ve. En «Mi equipo»
    // es en todos el que está mirando: buscar su apellido sacaría a todos.
    const autoriza = this.esRh ? s.autorizaNombre || "" : "";
    return `${s.colaborador || ""} ${s.area || ""} ${autoriza}`
      .toLowerCase().includes(t);
  }

  /**
   * El número de cada opción cuenta con el OTRO filtro y la búsqueda ya
   * puestos: con un área elegida, cada antigüedad dice cuántos de ESA área
   * hay. Antes contaba a toda la gente y «4 años · 18 días (3)» podía dar
   * «Nadie coincide». Las que darían cero salen en (0) y apagadas (la
   * plantilla), sin desaparecer: la lista no brinca al ir filtrando.
   */
  private cuentaFiltrosPersonas(t: string): void {
    const porArea = new Map<string, number>();
    const porTramo = new Map<string, number>();
    let total = 0;
    for (const s of this.saldos) {
      if (!this.pasaTextoPersona(s, t)) {
        continue;
      }
      if (this.pasaAntigPersona(s)) {
        total++;
        const a = this.claveArea(s);
        porArea.set(a, (porArea.get(a) || 0) + 1);
      }
      if (this.pasaAreaPersona(s)) {
        const tramo = this.tramoAntiguedad(s);
        porTramo.set(tramo, (porTramo.get(tramo) || 0) + 1);
      }
    }
    this.totalAreasPersonas = total;
    for (const a of this.areasPersonas) {
      a.n = porArea.get(a.valor) || 0;
    }
    for (const x of this.antigPersonas) {
      x.n = porTramo.get(x.valor) || 0;
    }
  }

  get hayFiltroPersona(): boolean {
    return !!this.fAreaPersona || !!this.fAntigPersona || !!this.fTextoPersona.trim();
  }

  limpiaPersonas(): void {
    this.fAreaPersona = "";
    this.fAntigPersona = "";
    this.fTextoPersona = "";
    this.filtraPersonas();
  }

  /**
   * Las opciones de Área y Antigüedad, sacadas de la gente que se ve, con
   * cuántos hay en cada una: sólo se ofrece lo que existe, y así un tramo
   * vacío no aparece como opción que no da nada. Si al cambiar de año la
   * opción elegida ya no existe, se suelta. El número que se enseña lo
   * rehace cuentaFiltrosPersonas con los otros filtros puestos.
   */
  private armaFiltrosPersonas(): void {
    const areas = new Map<string, number>();
    const tramos = new Map<string, number>();
    for (const s of this.saldos) {
      const a = this.claveArea(s);
      areas.set(a, (areas.get(a) || 0) + 1);
      const t = this.tramoAntiguedad(s);
      tramos.set(t, (tramos.get(t) || 0) + 1);
    }
    this.areasPersonas = Array.from(areas.entries())
      .filter(([a]) => a !== this.SIN_AREA)
      .map(([a, n]) => ({ valor: a, texto: a, n }))
      .sort((x, y) => x.texto.localeCompare(y.texto, "es"));
    if (areas.has(this.SIN_AREA)) {
      this.areasPersonas.push({ valor: this.SIN_AREA, texto: "Sin área", n: areas.get(this.SIN_AREA) || 0 });
    }
    this.antigPersonas = this.TRAMOS
      .filter((t) => tramos.has(t.valor))
      .map((t) => ({ ...t, n: tramos.get(t.valor) || 0 }));
    if (this.fAreaPersona && !areas.has(this.fAreaPersona)) {
      this.fAreaPersona = "";
    }
    if (this.fAntigPersona && !tramos.has(this.fAntigPersona)) {
      this.fAntigPersona = "";
    }
  }

  private claveArea(s: Saldo): string {
    const a = (s.area || "").trim();
    return a || this.SIN_AREA;
  }

  /**
   * El tramo de ley en el que cae su antigüedad. Sin antigüedad calculada
   * (periodo de año calendario) va aparte: ahí no hay tramo que decir, lo
   * mismo que la columna enseña «—».
   */
  private tramoAntiguedad(s: Saldo): string {
    if (!s.porAniversario || s.antiguedad == null) {
      return "sin";
    }
    const a = s.antiguedad;
    if (a <= 5) {
      return String(Math.max(a, 0));
    }
    if (a <= 10) {
      return "6-10";
    }
    if (a <= 15) {
      return "11-15";
    }
    if (a <= 20) {
      return "16-20";
    }
    if (a <= 25) {
      return "21-25";
    }
    return "26+";
  }

  /**
   * Un renglón por persona.
   *
   * El servidor ya manda uno solo (el periodo vigente a la fecha de
   * referencia), pero si alguna vez llegaran dos —el vigente y el que
   * sigue— la pestaña contaría doble y el área saldría de cualquiera. Se
   * queda el que contiene la fecha de referencia y, si ninguno, el primero
   * que llegó, en el orden en que llegó.
   */
  private unoPorPersona(saldos: Saldo[]): Saldo[] {
    const lista: Saldo[] = [];
    const lugar = new Map<number, number>();
    for (const s of saldos) {
      const id = s.idUsuario;
      if (id == null) {
        lista.push(s);
        continue;
      }
      const i = lugar.get(id);
      if (i === undefined) {
        lugar.set(id, lista.length);
        lista.push(s);
      } else if (!this.contieneRef(lista[i]) && this.contieneRef(s)) {
        lista[i] = s;
      }
    }
    return lista;
  }

  /** Si el periodo [inicio, fin) contiene la fecha de referencia. Se comparan textos ISO. */
  private contieneRef(s: Saldo): boolean {
    const ini = (s.periodoInicio || "").substring(0, 10);
    const fin = (s.periodoFin || "").substring(0, 10);
    return !!ini && !!fin && ini <= this.fechaRef && this.fechaRef < fin;
  }

  /**
   * «20/09/2026 al 20/09/2027», igual que las columnas «Inicio del
   * periodo» y «Fin del periodo» de la hoja de RH: el fin es el
   * aniversario siguiente, el día en que ya empieza el otro periodo.
   *
   * Un periodo de año calendario se dice así en vez de enseñar «01/01 al
   * 01/01», que parece un error. Y se dice por qué: el servidor marca
   * porAniversario por RENGLÓN (si ya tiene inicio de periodo), no por la
   * persona, así que también llega de calendario el de alguien CON Alta
   * IMSS cuyo periodo todavía no se pasa a su aniversario (un «Generado
   * para» del año que viene, p. ej.). Decirle «sin Alta IMSS» ahí
   * contradiría la columna de al lado.
   */
  textoPeriodo(s: Saldo): string {
    if (!s.porAniversario) {
      return s.fechaIngreso
        ? "año calendario · falta pasarlo a su aniversario"
        : "año calendario · sin Alta IMSS";
    }
    if (!s.periodoInicio || !s.periodoFin) {
      return "";
    }
    return `${this.fechaDma(s.periodoInicio)} al ${this.fechaDma(s.periodoFin)}`;
  }

  /** El tooltip del periodo: hasta qué día cuenta de verdad. */
  tipPeriodo(s: Saldo): string {
    if (!s.periodoInicio || !s.periodoFin) {
      return "";
    }
    const ultimo = this.fechaDma(this.diaAntes(s.periodoFin));
    if (!s.porAniversario) {
      const porque = s.fechaIngreso
        ? "Ya tiene Alta IMSS, pero este periodo todavía va por año calendario"
        : "Sin fecha de Alta IMSS su periodo es el año calendario";
      return `${porque}: del ${this.fechaDma(s.periodoInicio)} al ${ultimo}.`;
    }
    return `Del ${this.fechaDma(s.periodoInicio)} al ${ultimo}. ` +
      `El ${this.fechaDma(s.periodoFin)} cumple aniversario y empieza su siguiente periodo.`;
  }

  /**
   * «Cumple aniversario el 20/09», cuando al periodo en el que va hoy le
   * quedan 60 días o menos. Ya no hay «vencido»: el periodo vigente nunca
   * lo está, y lo que no tome antes se acumula a su siguiente periodo.
   * Los dos meses son el margen con el que RH alcanza a acomodarle el
   * descanso antes del aniversario.
   *
   * Sólo por aniversario: sin Alta IMSS no hay aniversario que avisar.
   * Y no a los dados de baja: el servidor los manda igual, pero a ellos
   * ya no se les abre el periodo siguiente, así que «se le acumula» sería
   * falso y RH no tiene descanso que acomodarle a quien ya no está.
   */
  avisoAniversario(s: Saldo): string {
    if (!s.porAniversario || !s.vigente || s.usuarioActivo === false) {
      return "";
    }
    const faltan = this.diasHasta(s.periodoFin);
    if (faltan === null || faltan <= 0 || faltan > 60) {
      return "";
    }
    return `Cumple aniversario el ${this.fechaDma(s.periodoFin).substring(0, 5)}`;
  }

  /** El porqué de la pastilla, con cuánto falta y cuánto se le acumularía. */
  tipAniversario(s: Saldo): string {
    const faltan = this.diasHasta(s.periodoFin) || 0;
    const cuando = faltan === 1 ? "Mañana" : `En ${faltan} días`;
    const quedan = this.quedan(s);
    if (quedan != null && quedan > 0) {
      return `${cuando}. Le quedan ${quedan} ${quedan === 1 ? "día" : "días"}: ` +
        "lo que no tome antes se acumula a su siguiente periodo.";
    }
    return `${cuando}. Lo que no tome antes se acumula a su siguiente periodo.`;
  }

  /**
   * Los años que cumplió al EMPEZAR el periodo, que son los que deciden
   * sus días de ley. Vacío en los periodos de año calendario: sin Alta
   * IMSS no hay antigüedad que calcular y un cero mentiría; y en el de
   * quien sí la tiene pero todavía no pasa a su aniversario, el servidor
   * la cuenta por año calendario, que no es «al empezar el periodo».
   */
  antiguedadTexto(s: Saldo): string {
    if (!s.porAniversario || s.antiguedad == null) {
      return "";
    }
    if (s.antiguedad === 0) {
      return "primer año";
    }
    return `${s.antiguedad} ${s.antiguedad === 1 ? "año" : "años"}`;
  }

  /** Por qué no hay antigüedad, para que el guion no parezca un dato perdido. */
  tipSinAntiguedad(s: Saldo): string {
    return s.fechaIngreso
      ? "Sale cuando su periodo pase a su aniversario"
      : "RH todavía no captura su Alta IMSS";
  }

  /** Los días de goce contra los de ley, por si RH los ajustó a mano. */
  tipGoce(s: Saldo): string {
    if (s.diasTotales == null) {
      return "Todavía no se le asignan días";
    }
    if (!s.porAniversario || s.diasPorLey == null) {
      return "";
    }
    const ley = `Por ley, con ${this.antiguedadTexto(s)}: ${s.diasPorLey} ${s.diasPorLey === 1 ? "día" : "días"}`;
    return s.diasTotales === s.diasPorLey ? ley : `${ley}. Aquí tiene ${s.diasTotales}.`;
  }

  /**
   * GOZADOS = los que tomó en COS + los que ya traía la hoja de RH (días sin
   * fecha, cuadrados al día de la carga). Es la columna «Días gozados» del
   * Excel. Desde el 20/10/2026 con «lo que le sobra primero» (../saldo-vista):
   * lo que pidió para su siguiente periodo cuenta en éste, si aquí le
   * sobraba. Igual que el calendario y «Mis vacaciones».
   */
  gozados(s: Saldo): number {
    return gozadosVista(s);
  }

  /** «Restantes», con «lo que le sobra primero»; null sin días asignados. */
  restantes(s: Saldo): number | null {
    return restantesVista(s);
  }

  /** «Acumulado», con «lo que le sobra primero»: lo que sobró de antes y no se ha usado. */
  acumulado(s: Saldo): number {
    return acumuladoVista(s) ?? 0;
  }

  /** «Le quedan», con «lo que le sobra primero»; null sin días asignados. */
  quedan(s: Saldo): number | null {
    return quedanVista(s);
  }

  /** "N en COS + M de la hoja de RH", para el tooltip de Gozados. */
  desgloseGozados(s: Saldo): string {
    const enCos = gozadosEnCos(s);
    const previos = s.diasGozadosPrevios || 0;
    // diasTomados aparta también lo que espera respuesta: se dice, o
    // alguien buscaría en el calendario un día que todavía no se autoriza.
    const cos = `${enCos} ${enCos === 1 ? "día" : "días"} en COS, contando lo que espera respuesta`;
    const corte = s.fechaCorte ? ` al ${this.fechaDma(s.fechaCorte)}` : "";
    const base = previos ? `${cos} + ${previos} de la hoja de RH${corte}` : cos;
    const delSiguiente = paraElSiguiente(s);
    if (!delSiguiente) {
      return base;
    }
    const cuantos = delSiguiente === enCos ? `Los ${delSiguiente}` : `De esos, ${delSiguiente}`;
    return `${base}. ${cuantos} son de lo que pidió para su siguiente periodo: se toman primero de lo que le sobra aquí.`;
  }

  /**
   * De dónde salió el acumulado: solo de su periodo anterior, o lo fijó RH;
   * y cuánto ya se usó. El cero va primero: el servidor lo marca como
   * calculado también cuando no hay periodo anterior, y ahí «le sobró» no
   * sería cierto.
   */
  tipAcumulado(s: Saldo): string {
    const a = this.acumulado(s);
    const usado = acumuladoUsado(s);
    if (a === 0) {
      return usado
        ? `Lo que le sobró de antes (${usado}) ya se usó: lo que le sobra se usa primero.`
        : "No trae días acumulados de periodos anteriores.";
    }
    const yaUsado = usado ? ` Ya se usaron ${usado}: lo que le sobra se usa primero.` : "";
    if (s.arrastreCalculado) {
      return a < 0
        ? `En su periodo anterior tomó ${-a} de más: se le descuentan de este.`
        : `Le sobró de su periodo anterior. Se calcula solo.${yaUsado}`;
    }
    return `Lo fijó RH (días pendientes de periodos anteriores).${yaUsado}`;
  }

  /**
   * "N restantes + M acumulados", para el tooltip de Le quedan. Los
   * números los manda el servidor: aquí no se vuelve a hacer la cuenta.
   */
  desgloseQuedan(s: Saldo): string {
    if (this.quedan(s) == null) {
      return "";
    }
    const r = this.restantes(s) || 0;
    const a = this.acumulado(s);
    const restantes = `${r} ${r === 1 ? "restante" : "restantes"} del periodo`;
    if (!a) {
      return restantes;
    }
    if (a < 0) {
      return `${restantes} − ${-a} que tomó de más en su periodo anterior`;
    }
    return `${restantes} + ${a} ${a === 1 ? "acumulado" : "acumulados"}`;
  }

  /* ===================== Documento (15/10/2026) ===================== */

  /** Sin FRC, sin nada subido y sin poder cargar: la celda dice «—». */
  sinDocVac(v: Vacacion): boolean {
    return !vacacionConFrc(v) && !v.firmado?.idFirmado && !rhCargaVacacion(v, this.miId);
  }

  sinDocPer(p: Permiso): boolean {
    return !permisoConFrc(p) && !p.firmado?.idFirmado && !rhCargaPermiso(p, this.miId);
  }

  /** El FRC-019 con los días hábiles de esta pantalla, que ya trae los festivos. */
  frcVacacion(v: Vacacion): void {
    if (v.idVacacion != null) {
      this.armaFrc("V" + v.idVacacion, "FRC-019", this.service.frc(v.idVacacion),
        (res) => res.vacaciones?.[0], (dato) => generaFrc019(dato, (t) => this.diasHabiles(t)));
    }
  }

  frcPermiso(p: Permiso): void {
    if (p.idPermiso != null) {
      this.armaFrc("P" + p.idPermiso, "FRC-017", this.permisosService.frc(p.idPermiso),
        (res) => res.permisos?.[0], generaFrc017);
    }
  }

  /**
   * Pide los datos con firmas y arma el PDF, como el Calendario y el
   * calendario de permisos. Uno solo para los dos formatos: sólo cambian
   * la petición, de dónde sale el dato y quién arma el PDF.
   */
  private armaFrc<R extends { codigoError?: string; mensajeError?: string }, T>(
    clave: string,
    doc: string,
    peticion: Observable<R>,
    dato: (res: R) => T | undefined,
    arma: (d: T) => void,
  ): void {
    this.generandoFrc = clave;
    peticion.subscribe({
      next: (res) => {
        this.generandoFrc = null;
        const d = dato(res);
        if (res.codigoError || !d) {
          this.aviso(res.mensajeError || `No se pudo armar el ${doc}.`);
        } else {
          arma(d);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.generandoFrc = null;
        this.aviso("No se pudo conectar con el servidor.");
        this.cdr.detectChanges();
      },
    });
  }

  private aviso(texto: string): void {
    swal.fire({ text: texto, icon: "error", confirmButtonText: "Entendido", confirmButtonColor: "#b71c1c" });
  }

  /* ===================== Paginado ===================== */

  totalPaginas(n: number): number {
    return Math.max(1, Math.ceil(n / this.POR_PAGINA));
  }

  /** Se acota al leer: los filtros cambian la cuenta sin avisar. */
  private paginaValida(pagina: number, n: number): number {
    return Math.min(Math.max(1, pagina), this.totalPaginas(n));
  }

  get vacPagina(): Vacacion[] {
    const desde = (this.paginaValida(this.paginaVac, this.vacFiltradas.length) - 1) * this.POR_PAGINA;
    return this.vacFiltradas.slice(desde, desde + this.POR_PAGINA);
  }

  get perPagina(): Permiso[] {
    const desde = (this.paginaValida(this.paginaPer, this.perFiltrados.length) - 1) * this.POR_PAGINA;
    return this.perFiltrados.slice(desde, desde + this.POR_PAGINA);
  }

  get personasPagina(): Saldo[] {
    const desde = (this.paginaValida(this.paginaPersona, this.personasFiltradas.length) - 1) * this.POR_PAGINA;
    return this.personasFiltradas.slice(desde, desde + this.POR_PAGINA);
  }

  rango(pagina: number, n: number): string {
    if (!n) {
      return "0";
    }
    const p = this.paginaValida(pagina, n);
    const desde = (p - 1) * this.POR_PAGINA + 1;
    const hasta = Math.min(p * this.POR_PAGINA, n);
    return `${desde}–${hasta} de ${n}`;
  }

  /* ===================== Exportar ===================== */

  exportaVacaciones(): void {
    if (!this.vacFiltradas.length) {
      return;
    }
    const cab = [
      "Colaborador", "Área", "Desde", "Hasta", "Regresa", "Días",
      "Estatus", "Origen", "Pedida", "Resolvió", "Fecha resolución",
      "Motivo", "Comentario", ...this.colDocumento(),
    ];
    const lineas = [cab.map(this.csv).join(",")];
    for (const v of this.vacFiltradas) {
      lineas.push([
        v.colaborador, this.areaDe(v.idUsuario), v.fechaInicio, v.fechaFin,
        v.fechaRegreso, this.diasHabiles(v), this.etiquetaVac(v.estatus),
        v.origen === "SOLICITUD" ? "Pedida" : "Capturada",
        v.fechaCreacion, v.autorizaNombre, v.fechaResolucion,
        v.motivo, notaVisible(v.comentario), ...this.docVacacion(v),
      ].map(this.csv).join(","));
    }
    this.descarga(lineas, `vacaciones-${this.esRh ? "todos" : "mi-equipo"}-${this.anio}.csv`);
  }

  exportaPermisos(): void {
    if (!this.perFiltrados.length) {
      return;
    }
    const cab = [
      "Colaborador", "Área", "Día", "Permiso", "Hora", "Goce",
      "Estatus", "Pedido", "Resolvió", "Fecha resolución",
      "Motivo", "Observaciones", ...this.colDocumento(),
    ];
    const lineas = [cab.map(this.csv).join(",")];
    for (const p of this.perFiltrados) {
      const tipo = this.TIPOS.find((t) => t.valor === p.tipo);
      lineas.push([
        p.colaborador, p.area || this.areaDe(p.idUsuario), p.fecha,
        tipo ? tipo.texto : p.tipo, p.hora,
        p.goce === "CON" ? "Con goce" : p.goce === "SIN" ? "Sin goce" : "",
        this.etiquetaPer(p.estatus), p.fechaCreacion, p.autorizaNombre,
        p.fechaResolucion, p.motivo, p.observaciones, ...this.docPermiso(p),
      ].map(this.csv).join(","));
    }
    this.descarga(lineas, `permisos-${this.esRh ? "todos" : "mi-equipo"}-${this.anio}.csv`);
  }

  /**
   * Personas con las columnas de la hoja de RH, para cuadrarla renglón
   * por renglón. Las fechas del periodo van como las trae el Excel (el fin
   * es el aniversario siguiente) y los gozados van desglosados, que es
   * justo lo que hay que mirar cuando una persona no cuadra. El archivo
   * lleva la fecha de referencia: son los periodos vigentes a ese día.
   */
  exportaPersonas(): void {
    if (!this.personasFiltradas.length) {
      return;
    }
    const cab = [
      "Colaborador", "Área", "Autoriza", "Alta IMSS", "Periodo",
      "Inicio del periodo", "Fin del periodo", "Antigüedad", "Días de goce",
      "Gozados en COS", "Gozados fuera de COS", "Días gozados", "Días restantes",
      "Acumulado", "Acumulado de", "Le quedan", "Estatus",
    ];
    const lineas = [cab.map(this.csv).join(",")];
    for (const s of this.personasFiltradas) {
      lineas.push([
        s.colaborador, s.area, s.autorizaNombre, s.fechaIngreso,
        // Igual que en pantalla: «sin Alta IMSS» sólo si de verdad falta.
        s.porAniversario ? "Aniversario"
          : s.fechaIngreso ? "Año calendario (falta pasarlo a su aniversario)"
          : "Año calendario (sin Alta IMSS)",
        s.periodoInicio, s.periodoFin,
        s.porAniversario ? s.antiguedad : null,
        s.diasTotales, gozadosEnCos(s), s.diasGozadosPrevios || 0, this.gozados(s),
        this.restantes(s), this.acumulado(s),
        this.origenAcumulado(s),
        this.quedan(s),
        s.usuarioActivo === false ? "Baja" : "Activo",
      ].map(this.csv).join(","));
    }
    this.descarga(lineas,
      `personas-${this.esRh ? "todos" : "mi-equipo"}-periodos-al-${this.fechaRef}.csv`);
  }

  /** La columna «Acumulado de» del CSV: de su periodo anterior o lo fijó RH; vacía sin acumulado. */
  private origenAcumulado(s: Saldo): string {
    if (!this.acumulado(s)) {
      return "";
    }
    return s.arrastreCalculado ? "Su periodo anterior" : "RH";
  }

  /**
   * La columna «Documento» del CSV (15/10/2026): sólo en Seguimiento RH,
   * que es donde se ve en la tabla. En «Mi equipo» el archivo no cambia.
   */
  private colDocumento(): string[] {
    return this.esRh ? ["Documento"] : [];
  }

  private docVacacion(v: Vacacion): string[] {
    return this.esRh ? [textoDocumento(vacacionConFrc(v), v.enPapel, v.firmado)] : [];
  }

  private docPermiso(p: Permiso): string[] {
    return this.esRh ? [textoDocumento(permisoConFrc(p), p.enPapel, p.firmado)] : [];
  }

  /** Las comillas se duplican, que es como se escapan en CSV. */
  private csv = (x: unknown): string =>
    `"${String(x == null ? "" : x).replaceAll('"', '""')}"`;

  private descarga(lineas: string[], nombre: string): void {
    // El BOM va primero o Excel abre los acentos como jeroglíficos.
    const csv = "﻿" + lineas.join("\r\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = nombre;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* ===================== Presentación ===================== */

  areaDe(idUsuario?: number): string {
    return idUsuario != null ? this.areaPor.get(idUsuario) || "" : "";
  }

  /** "12 nov – 20 nov"; con días sueltos, cada tramo: "2 nov – 4 nov y 23 nov". */
  textoRango(v: Vacacion): string {
    if (v.tramos && v.tramos.length > 1) {
      const textos = v.tramos.map((t) => this.textoRango(t));
      return `${textos.slice(0, -1).join(", ")} y ${textos.at(-1)}`;
    }
    if (!v.fechaInicio || !v.fechaFin) {
      return "";
    }
    if (v.fechaInicio === v.fechaFin) {
      return this.fechaCorta(v.fechaInicio);
    }
    return `${this.fechaCorta(v.fechaInicio)} – ${this.fechaCorta(v.fechaFin)}`;
  }

  /** "12 nov" */
  fechaCorta(iso?: string): string {
    if (!iso || iso.length < 10) {
      return iso || "";
    }
    const m = Number.parseInt(iso.substring(5, 7), 10) - 1;
    const d = Number.parseInt(iso.substring(8, 10), 10);
    return `${d} ${this.MESES_CORTOS[m] || ""}`;
  }

  /**
   * "20/09/2026", como las fechas de la hoja de RH. Se parte el texto y
   * no se hace un Date: un día no tiene hora y así no hay zona horaria
   * que lo corra.
   */
  fechaDma(iso?: string | null): string {
    if (!iso || iso.length < 10) {
      return iso || "";
    }
    return `${iso.substring(8, 10)}/${iso.substring(5, 7)}/${iso.substring(0, 4)}`;
  }

  /** El día anterior (ISO): el último día que de verdad es del periodo. */
  private diaAntes(iso: string): string {
    const d = this.aFecha(iso);
    d.setDate(d.getDate() - 1);
    return this.iso(d);
  }

  /** Cuántos días faltan de hoy a esa fecha. Negativo si ya pasó. */
  private diasHasta(iso?: string | null): number | null {
    if (!iso || iso.length < 10) {
      return null;
    }
    const hoy = this.aFecha(this.iso(new Date()));
    // Math.round y no floor: el cambio de horario de verano mete o quita
    // una hora entre dos medianoches.
    return Math.round((this.aFecha(iso).getTime() - hoy.getTime()) / 86400000);
  }

  /** "12/09 14:30" a partir de "2026-09-12 14:30". */
  fechaHoraCorta(t?: string): string {
    if (!t || t.length < 10) {
      return t || "";
    }
    const dia = `${t.substring(8, 10)}/${t.substring(5, 7)}`;
    return t.length >= 16 ? `${dia} ${t.substring(11, 16)}` : dia;
  }

  /** Los días hábiles del periodo: ni fin de semana ni festivo. Igual que el servidor. De un pedido de días sueltos, los de sus tramos. */
  diasHabiles(v: Vacacion): number {
    return tramosDe(v).reduce((n, t) => n + this.habilesDelRango(t), 0);
  }

  private habilesDelRango(v: Vacacion): number {
    if (!v.fechaInicio || !v.fechaFin) {
      return 0;
    }
    let n = 0;
    const fin = this.aFecha(v.fechaFin);
    const cursor = this.aFecha(v.fechaInicio);
    while (cursor <= fin) {
      const d = cursor.getDay();
      if (d !== 0 && d !== 6 && !this.festivos.has(this.iso(cursor))) {
        n++;
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return n;
  }

  /** Un color con transparencia, para el relleno de la etiqueta. */
  tinte(color: string | undefined, alpha: number): string {
    const c = color || "#0369a1";
    const n = c.replace("#", "");
    const r = Number.parseInt(n.substring(0, 2), 16);
    const g = Number.parseInt(n.substring(2, 4), 16);
    const b = Number.parseInt(n.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  trackVacacion(_: number, v: Vacacion): number | undefined {
    return v.idVacacion;
  }

  trackPermiso(_: number, p: Permiso): number | undefined {
    return p.idPermiso;
  }

  trackSaldo(_: number, s: Saldo): number | undefined {
    return s.idUsuario;
  }

  private iso(d: Date): string {
    const m = `${d.getMonth() + 1}`.padStart(2, "0");
    const dia = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${m}-${dia}`;
  }

  /** ISO a Date local, sin que la zona horaria mueva el día. */
  private aFecha(iso: string): Date {
    const [a, m, d] = iso.substring(0, 10).split("-").map((x) => Number.parseInt(x, 10));
    return new Date(a, m - 1, d);
  }
}
